<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import { isPhoneE164OrEmpty, isNumber } from 'shared/helpers/Validators';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import { initFB } from 'shared/helpers/facebookInitializer';
import AccountAPI from '../../../../../api/account';
import { loadScript } from 'dashboard/helper/DOMHelpers';
import * as Sentry from '@sentry/vue';

export default {
  components: {
    LoadingState,
  },
  mixins: [globalConfigMixin],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      phoneNumber: '',
      apiKey: '',
      phoneNumberId: '',
      businessAccountId: '',
      hasLoginStarted: false,
      emptyStateMessage: this.$t('INBOX_MGMT.DETAILS.LOADING_FB'),
      isFbConnected: false,
      selectedPhoneNumber: {},
      phone_numbers: [],
      featureType: 'whatsapp_business_app_onboarding',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  watch: {
    selectedPhoneNumber(newVal) {
      if (newVal) {
        this.phoneNumberId = newVal.id;
        this.phoneNumber = this.formatPhoneNumber(
          `+${newVal.display_phone_number}`
        );
      }
    },
  },
  created() {
    // loadFBsdk();
  },
  mounted() {
    window.fbAsyncInit = initFB;
  },
  validations: {
    inboxName: { required },
    phoneNumber: { required, isPhoneE164OrEmpty },
    apiKey: { required },
    phoneNumberId: { required, isNumber },
    businessAccountId: { required, isNumber },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const whatsappChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.inboxName,
            channel: {
              type: 'whatsapp',
              phone_number: this.phoneNumber,
              provider: 'whatsapp_cloud',
              provider_config: {
                api_key: this.apiKey,
                phone_number_id: this.phoneNumberId,
                business_account_id: this.businessAccountId,
                type: 'whatsapp_embedded',
              },
            },
          }
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: whatsappChannel.id,
          },
        });
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
        );
      }
    },
    async startLogin() {
      this.hasLoginStarted = true;

      try {
        // this will load the SDK in a promise, and resolve it when the sdk is loaded
        // in case the SDK is already present, it will resolve immediately
        await this.loadFBsdk();
        initFB(); // run init anyway, `initFB` won't wait for `fbAsyncInit` otherwise.
        this.tryFBlogin(); // make an attempt to login
      } catch (error) {
        if (error.name === 'ScriptLoaderError') {
          // if the error was related to script loading, we show a toast
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
        } else {
          // if the error was anything else, we capture it and show a toast
          Sentry.captureException(error);
          console.log(error);
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
        }
      }
    },
    showLoader() {
      return !this.user_access_token || this.isCreating;
    },
    async loadFBsdk() {
      return loadScript('https://connect.facebook.net/en_US/sdk.js', {
        id: 'facebook-jssdk',
      });
    },
    tryFBlogin() {
      window.addEventListener('message', event => {
        if (
          !['https://www.facebook.com', 'https://web.facebook.com'].includes(
            event.origin
          )
        ) {
          return;
        }
        try {
          const data = JSON.parse(event.data);
          console.log('data -> ', data);
          if (data.type === 'WA_EMBEDDED_SIGNUP') {
            // if user finishes the Embedded Signup flow
            if (
              data.event === 'FINISH' ||
              data.event === 'FINISH_ONLY_WABA' ||
              data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
            ) {
              const { phone_number_id, waba_id } = data.data;
              console.log('Phone number ID ', phone_number_id, ' WhatsApp business account ID ', waba_id);
              this.phoneNumberId = phone_number_id;
              this.businessAccountId = waba_id;
              // if user cancels the Embedded Signup flow
            } else if (data.event === 'CANCEL') {
              const { current_step } = data.data;
              useAlert(`Fluxo de login foi abandonado em: ${current_step}`);
              // if user reports an error during the Embedded Signup flow
            } else if (data.event === 'ERROR') {
              const { error_message } = data.data;
              useAlert(`Erro ao logar: ${error_message}`);
            }
          }
          useAlert(JSON.stringify(data, null, 2));
        } catch {
          console.log('Non JSON Responses', event.data);
        }
      });

      // eslint-disable-next-line no-undef
      FB.login(
        response => {
          if (response.status === 'connected') {
            // Use an async IIFE inside the synchronous callback
            (async () => {
              try {
                const WABAIDresponse = await AccountAPI.getWABAID(
                  response.authResponse.code
                );
                if (WABAIDresponse.data.error) {
                  console.error('Error fetching WABA ID:', WABAIDresponse.data.error);
                  useAlert(WABAIDresponse.data.error);
                  this.emptyStateMessage = WABAIDresponse.data.error;
                } else {
                  this.businessAccountId = WABAIDresponse.data.waba_id;
                  this.phone_numbers = WABAIDresponse.data.phone_numbers;
                  this.isFbConnected = true;
                  this.apiKey = WABAIDresponse.data.api_key;
                }
              } catch (error) {
                console.error('Error fetching WABA ID:', error);
                useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
              }
            })(); // End of async IIFE
          } else if (response.status === 'not_authorized') {
            this.emptyStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_AUTH'
            );
          } else {
            this.emptyStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_AUTH'
            );
          }
        },
        {
          config_id: window.chatwootConfig.fbConfigID,
          response_type: 'code',
          override_default_response_type: true,
          // scope: 'business_management, whatsapp_business_management, whatsapp_business_messaging, public_profile',
          // scope: 'whatsapp_business_management,whatsapp_business_messaging,business_management',
          extras: {
            // feature: 'whatsapp_embedded_signup',
            version: 'v3',
            featureType: this.featureType, // 'whatsapp_business_app_onboarding', 'only_waba_sharing', 'whatsapp_embedded_signup'
            sessionInfoVersion: '3',
            // version: 3,
            setup: {},
          },
        }
      );
    },
    formatPhoneNumber(phoneNumber) {
      return `+${phoneNumber.replace(/\D/g, '')}`;
    },
  },
};
</script>

<template>
  <form class="flex flex-wrap mx-0" @submit.prevent="createChannel()">
    <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
      <label>
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.FEATURE_TYPE.LABEL') }}
        <select v-model="featureType">
          <option value="whatsapp_business_app_onboarding">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.FEATURE_TYPE.WHATSAPP_BUSINESS_APP_ONBOARDING'
              )
            }}
          </option>
          <option value="only_waba_sharing">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.FEATURE_TYPE.ONLY_WABA_SHARING') }}
          </option>
          <option value="whatsapp_embedded_signup">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.FEATURE_TYPE.NONE') }}
          </option>
        </select>
      </label>
    </div>
    <div
      v-if="apiKey !== ''"
      class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]"
    >
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div
      v-if="apiKey !== ''"
      class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]"
    >
      <label :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
        <select
          v-model.trim="selectedPhoneNumber"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          @blur="v$.phoneNumber.$touch"
        >
          <option value="">Selecione</option>
          <option v-for="phone in phone_numbers" :key="phone.id" :value="phone">
            {{ phone.display_phone_number }}
          </option>
        </select>
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
        </span>
      </label>
    </div>

    <div
      v-if="apiKey !== ''"
      class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]"
    >
      <label :class="{ error: v$.phoneNumberId.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.LABEL') }}
        </span>
        <input
          v-model="phoneNumberId"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.PLACEHOLDER')
          "
          disabled
          @blur="v$.phoneNumberId.$touch"
        />
        <span v-if="v$.phoneNumberId.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.ERROR') }}
        </span>
      </label>
    </div>

    <div
      v-if="apiKey !== ''"
      class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]"
    >
      <label :class="{ error: v$.businessAccountId.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.LABEL') }}
        </span>
        <input
          v-model="businessAccountId"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.PLACEHOLDER')
          "
          disabled
          @blur="v$.businessAccountId.$touch"
        />
        <span v-if="v$.businessAccountId.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.ERROR') }}
        </span>
      </label>
    </div>
    <div
      v-if="apiKey === ''"
      class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]"
    >
      <div
        v-if="!hasLoginStarted"
        class="login-init text-left medium-8 mb-1 columns p-0"
      >
        <a href="#" @click="startLogin()">
          <img
            src="~dashboard/assets/images/channels/facebook_login.png"
            alt="Facebook-logo"
          />
        </a>
      </div>
      <div v-else class="login-init medium-8 columns p-0">
        <LoadingState v-if="showLoader" :message="emptyStateMessage" />
      </div>
    </div>
    <div class="w-full">
      <woot-submit-button
        :loading="uiFlags.isCreating"
        :button-text="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
      />
    </div>
  </form>
</template>

<style scoped>
.p-0 {
  padding: 0%;
}
.text-left {
  text-align: left;
}

.mb-1 {
  margin-bottom: 1.6rem;
}
</style>
